import React, { useState } from "react";
import DenominationPicker from "../ui/DenominationPicker";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";

/**
 * Org onboarding beliefs: denomination + full Nicene Creed affirmation.
 * @param {Object} props
 * @param {Object} [props.formData]
 * @param {Object} props.beliefs
 * @param {function(Object): void} props.onBeliefsUpdate
 * @param {function(): void} props.onBack
 * @param {function(): void} props.onNext
 * @param {string} [props.subjectLabel]
 * @returns {JSX.Element}
 */
export default function Beliefs({ formData, beliefs, onBeliefsUpdate, onBack, onNext, subjectLabel }) {
    const displayName = formData?.name || "your organization";
    const isIndividual = subjectLabel === "your";
    const denominationChosen = Boolean((beliefs?.denomination || "").trim());
    const creedAgreed = Boolean(beliefs?.niceneCreedAgreed);
    const canContinue = denominationChosen && creedAgreed;
    const [showDenominationError, setShowDenominationError] = useState(false);

    /**
     * @param {string} key
     * @param {*} value
     */
    const handleBeliefChange = (key, value) => {
        onBeliefsUpdate({ ...beliefs, [key]: value });
        if (key === "denomination" && String(value || "").trim()) {
            setShowDenominationError(false);
        }
    };

    /**
     * @returns {void}
     */
    const handleNext = () => {
        if (!denominationChosen) {
            setShowDenominationError(true);
            document.getElementById("signup-denomination")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            document.getElementById("org-onboarding-denomination")?.focus();
            return;
        }
        if (!creedAgreed) return;
        onNext();
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="mb-2 text-2xl font-bold text-text-primary">
                    {isIndividual ? "Your beliefs" : "Organization Beliefs"}
                </h2>
                <p className="mb-6 text-text-secondary">
                    {isIndividual
                        ? "Share your Christian beliefs and denominational affiliation"
                        : `Share ${displayName}'s Christian beliefs and denominational affiliation`}
                </p>
            </div>

            <div id="signup-denomination" className="scroll-mt-24 rounded-lg border border-border p-4">
                <h3 className="mb-4 text-lg font-semibold text-text-primary">Denomination</h3>
                <Label htmlFor="org-onboarding-denomination" className="mb-2 block text-sm text-text-secondary">
                    Search or select denomination <span className="text-error">*</span>
                </Label>
                <DenominationPicker
                    id="org-onboarding-denomination"
                    value={beliefs?.denomination || ""}
                    onChange={(denomination) => handleBeliefChange("denomination", denomination)}
                    required
                    invalid={showDenominationError}
                />
                {showDenominationError ? (
                    <p className="mt-2 text-sm text-error" role="alert">
                        Denomination is required
                    </p>
                ) : null}
            </div>

            <div className="rounded-lg border border-border p-4">
                <h3 className="mb-4 text-lg font-semibold text-text-primary">Nicene Creed</h3>
                <div className="prose prose-invert mb-4 max-w-none text-text-secondary">
                    <p>
                        We believe in one God,
                        <br />
                        the Father, the Almighty,
                        <br />
                        maker of heaven and earth,
                        <br />
                        of all that is, seen and unseen.
                        <br />
                        <br />
                        We believe in one Lord, Jesus Christ,
                        <br />
                        the only Son of God,
                        <br />
                        eternally begotten of the Father,
                        <br />
                        God from God, Light from Light,
                        <br />
                        true God from true God,
                        <br />
                        begotten, not made,
                        <br />
                        of one Being with the Father.
                        <br />
                        Through him all things were made.
                        <br />
                        For us and for our salvation
                        <br />
                        he came down from heaven:
                        <br />
                        by the power of the Holy Spirit
                        <br />
                        he became incarnate from the Virgin Mary,
                        <br />
                        and was made man.
                        <br />
                        For our sake he was crucified under Pontius Pilate;
                        <br />
                        he suffered death and was buried.
                        <br />
                        On the third day he rose again
                        <br />
                        in accordance with the Scriptures;
                        <br />
                        he ascended into heaven
                        <br />
                        and is seated at the right hand of the Father.
                        <br />
                        He will come again in glory to judge the living and the dead,
                        <br />
                        and his kingdom will have no end.
                        <br />
                        <br />
                        We believe in the Holy Spirit, the Lord, the giver of life,
                        <br />
                        who proceeds from the Father.
                        <br />
                        With the Father and the Son he is worshiped and glorified.
                        <br />
                        He has spoken through the Prophets.
                        <br />
                        We believe in one holy catholic and apostolic Church.
                        <br />
                        We acknowledge one baptism for the forgiveness of sins.
                        <br />
                        We look for the resurrection of the dead,
                        <br />
                        and the life of the world to come. Amen.
                    </p>
                </div>
                <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                        id="nicene-creed-agree"
                        checked={!!beliefs.niceneCreedAgreed}
                        onCheckedChange={(checked) => handleBeliefChange("niceneCreedAgreed", checked)}
                        className="mt-1 h-5 w-5"
                    />
                    <label htmlFor="nicene-creed-agree" className="select-none text-text-secondary">
                        {displayName} affirms the Nicene Creed
                    </label>
                </div>
            </div>

            <div className="flex justify-between border-t border-border pt-6">
                <button
                    type="button"
                    onClick={onBack}
                    className="rounded-md border border-border bg-secondary px-6 py-2 text-text-primary transition hover:bg-tertiary"
                >
                    Back
                </button>
                <button
                    type="button"
                    onClick={handleNext}
                    disabled={denominationChosen && !canContinue}
                    className={`rounded-md px-6 py-2 transition ${
                        denominationChosen && !canContinue
                            ? "cursor-not-allowed bg-secondary text-text-secondary opacity-50"
                            : "cursor-pointer bg-accent text-text-on-accent hover:bg-accent-hover"
                    }`}
                >
                    Next
                </button>
            </div>
        </div>
    );
}
